import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "wagmi";
import {
  Rocket,
  Image,
  Loader2,
  ExternalLink,
  ArrowLeft,
  TrendingUp,
  Zap,
  BarChart3,
  Lock,
  CheckCircle2,
  Copy,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useLaunchToken, useLaunchpadConfig } from "../../hooks/useLaunchpad";
import { formatTokenAmount, parseTokenAmount, explorerUrl, cn } from "../../lib/utils";

// ─── Inline token preview (no dependency on TokenCard's Link wrapper) ─────────

interface TokenPreviewProps {
  name: string;
  symbol: string;
  description: string;
  imageUri: string;
  buyFee: bigint;
  sellFee: bigint;
  graduationThreshold: bigint;
}

function TokenPreview({
  name,
  symbol,
  description,
  imageUri,
  buyFee,
  sellFee,
  graduationThreshold,
}: TokenPreviewProps) {
  const displayName = name.trim() || "Token Name";
  const displaySymbol = symbol.trim() || "TKN";
  const displayDesc = description.trim() || "Your token description will appear here.";
  const initials = displaySymbol.slice(0, 2).toUpperCase();

  return (
    <div className="glass-card p-4 glow-orange">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-14 h-14 rounded-card overflow-hidden bg-surface border border-border flex-shrink-0">
          {imageUri ? (
            <img
              src={imageUri}
              alt={displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xl font-bold text-primary bg-primary/10 font-display">
              {initials}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-text-primary truncate">
              {displayName}
            </h3>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-primary/10 text-primary border-primary/20">
              LIVE
            </span>
          </div>
          <span className="text-xs text-text-tertiary">${displaySymbol}</span>
        </div>
      </div>

      <p className="text-xs text-text-tertiary line-clamp-2 mb-3">{displayDesc}</p>

      {/* Bonding curve progress bar at 0% */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-text-tertiary">Bonding Curve</span>
          <span className="text-text-secondary font-medium">0%</span>
        </div>
        <div className="w-full h-1.5 bg-surface rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: "0%", background: "linear-gradient(90deg, #F7931A, #FF6EC7)" }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Buy Fee</div>
          <div className="text-primary font-semibold">
            {buyFee > 0n ? (Number(buyFee) / 100).toFixed(2) + "%" : "—"}
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Sell Fee</div>
          <div className="text-primary font-semibold">
            {sellFee > 0n ? (Number(sellFee) / 100).toFixed(2) + "%" : "—"}
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Grad. at</div>
          <div className="text-text-secondary font-semibold">
            {formatTokenAmount(graduationThreshold)} JASMY
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Chain</div>
          <div className="text-jasmy font-semibold">JasmyChain 680</div>
        </div>
      </div>

      {/* Feature badges */}
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
          <TrendingUp className="w-2.5 h-2.5" /> Bonding Curve
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary/10 text-secondary border border-secondary/20">
          <Zap className="w-2.5 h-2.5" /> Auto-LP
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-surface-hover text-text-secondary border border-border">
          <BarChart3 className="w-2.5 h-2.5" /> Jasmy Swap
        </span>
      </div>
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-button bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <div>
        <h3 className="font-display font-semibold text-text-primary text-sm">{title}</h3>
        {subtitle && <p className="text-xs text-text-tertiary mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Feature toggle card ─────────────────────────────────────────────────────

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  active: boolean;
  locked?: boolean;
  onClick?: () => void;
}

function FeatureCard({ icon: Icon, title, description, active, locked, onClick }: FeatureCardProps) {
  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      className={cn(
        "w-full text-left p-3 rounded-card border transition-all duration-200",
        locked
          ? "cursor-default border-primary/30 bg-primary/5"
          : active
          ? "border-primary/40 bg-primary/8 cursor-pointer hover:border-primary/60"
          : "border-border bg-surface/50 cursor-pointer hover:border-border-bright"
      )}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            "w-7 h-7 rounded-button flex items-center justify-center flex-shrink-0 mt-0.5",
            active || locked ? "bg-primary/15 text-primary" : "bg-surface text-text-tertiary"
          )}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-semibold", active || locked ? "text-text-primary" : "text-text-secondary")}>
              {title}
            </span>
            {locked && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25 uppercase tracking-wide">
                Always On
              </span>
            )}
            {!locked && (
              <div
                className={cn(
                  "w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center transition-all",
                  active ? "bg-primary border-primary" : "border-text-tertiary"
                )}
              >
                {active && <Check className="w-2 h-2 text-background" strokeWidth={3} />}
              </div>
            )}
          </div>
          <p className="text-[10px] text-text-tertiary mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
    </button>
  );
}

// ─── Success modal ───────────────────────────────────────────────────────────

interface SuccessModalProps {
  name: string;
  symbol: string;
  txHash: string;
  onReset: () => void;
}

function SuccessModal({ name, symbol, txHash, onReset }: SuccessModalProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  function copyHash() {
    navigator.clipboard.writeText(txHash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="glass-card p-8 max-w-md w-full text-center glow-orange animate-slide-up">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-primary" />
        </div>

        <h2 className="font-display text-2xl font-bold text-text-primary mb-1">Token Launched</h2>
        <p className="text-text-secondary text-sm mb-6">
          <span className="text-text-primary font-semibold">{name}</span>
          <span className="text-text-tertiary"> (${symbol}) </span>
          is now live on JasmyChain
        </p>

        {/* TX hash */}
        <div className="bg-surface/50 border border-border rounded-card p-3 mb-6 flex items-center gap-2">
          <span className="text-xs text-text-tertiary font-mono flex-1 truncate">{txHash}</span>
          <button
            type="button"
            onClick={copyHash}
            className="text-text-tertiary hover:text-primary transition-colors flex-shrink-0"
          >
            {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <a
            href={explorerUrl("tx", txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-flex items-center justify-center gap-2 text-sm"
          >
            View on Explorer <ExternalLink className="w-4 h-4" />
          </a>
          <button
            type="button"
            onClick={() => navigate("/swap")}
            className="btn-primary inline-flex items-center justify-center gap-2 text-sm"
          >
            Trade Now <TrendingUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onReset}
            className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
          >
            Deploy another token
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main LaunchForm ─────────────────────────────────────────────────────────

export function LaunchForm() {
  const { isConnected } = useAccount();
  const { launchFee, buyFeeBps, sellFeeBps, graduationThreshold } = useLaunchpadConfig();
  const { launch, isPending, isConfirming, isSuccess, txHash } = useLaunchToken();

  // Form state
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [description, setDescription] = useState("");
  const [imageUri, setImageUri] = useState("");
  const [initialBuy, setInitialBuy] = useState("");
  const [wantsInitialBuy, setWantsInitialBuy] = useState(false);

  function resetForm() {
    setName("");
    setSymbol("");
    setDescription("");
    setImageUri("");
    setInitialBuy("");
    setWantsInitialBuy(false);
  }

  const parsedInitialBuy = wantsInitialBuy ? parseTokenAmount(initialBuy) : 0n;
  const totalCost = launchFee + parsedInitialBuy;

  const isValid =
    name.trim().length >= 1 &&
    name.trim().length <= 32 &&
    symbol.trim().length >= 1 &&
    symbol.trim().length <= 10 &&
    description.trim().length <= 500;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || !isConnected) return;
    launch(name.trim(), symbol.trim().toUpperCase(), description.trim(), imageUri.trim(), parsedInitialBuy);
  }

  // Success overlay
  if (isSuccess && txHash) {
    return (
      <SuccessModal
        name={name}
        symbol={symbol}
        txHash={txHash}
        onReset={resetForm}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-20">
      {/* Back link */}
      <Link
        to="/launchpad"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Launchpad
      </Link>

      {/* Page header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold gradient-text mb-1">Token Factory</h1>
        <p className="text-text-secondary text-sm">
          Configure and launch your token on the JUMP_FUD bonding curve
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* ── Left panel: wizard form ────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} className="lg:col-span-3 space-y-5">

          {/* Section 1: Token Identity */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Image}
              title="Token Identity"
              subtitle="Name and visual identity of your token"
            />
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Token Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Awesome Token"
                  className="input-field"
                  maxLength={32}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{name.length}/32</div>
              </div>

              {/* Symbol */}
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Symbol <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="MAT"
                  className="input-field uppercase tracking-widest"
                  maxLength={10}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{symbol.length}/10</div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your token's purpose, community, or meme..."
                  className="input-field resize-none h-24"
                  maxLength={500}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{description.length}/500</div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Icon URL</label>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-card bg-surface border border-border flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {imageUri ? (
                      <img
                        src={imageUri}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <Image className="w-5 h-5 text-text-tertiary" />
                    )}
                  </div>
                  <input
                    type="url"
                    value={imageUri}
                    onChange={(e) => setImageUri(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="input-field"
                  />
                </div>
                <p className="text-[11px] text-text-tertiary mt-1">
                  Use a square image (1:1), minimum 200×200px recommended.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Fee Structure (read-only from contract) */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={BarChart3}
              title="Fee Structure"
              subtitle="Contract-enforced fees — set by the JUMP_FUD factory"
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-surface/50 border border-border rounded-card p-3">
                <div className="text-xs text-text-tertiary mb-1">Buy Fee</div>
                <div className="text-lg font-bold font-display text-primary">
                  {buyFeeBps > 0n ? (Number(buyFeeBps) / 100).toFixed(2) + "%" : "Loading..."}
                </div>
                <div className="text-[10px] text-text-tertiary mt-1">Applied on each buy</div>
              </div>
              <div className="bg-surface/50 border border-border rounded-card p-3">
                <div className="text-xs text-text-tertiary mb-1">Sell Fee</div>
                <div className="text-lg font-bold font-display text-primary">
                  {sellFeeBps > 0n ? (Number(sellFeeBps) / 100).toFixed(2) + "%" : "Loading..."}
                </div>
                <div className="text-[10px] text-text-tertiary mt-1">Applied on each sell</div>
              </div>
            </div>
            <p className="text-[11px] text-text-tertiary mt-3 flex items-start gap-1.5">
              <Lock className="w-3 h-3 text-text-tertiary flex-shrink-0 mt-0.5" />
              Fees are set by the factory contract and apply to all tokens equally. Not adjustable per token.
            </p>
          </div>

          {/* Section 3: Features */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Zap}
              title="Features"
              subtitle="Mechanics that govern your token's lifecycle"
            />
            <div className="space-y-2.5">
              <FeatureCard
                icon={TrendingUp}
                title="Bonding Curve"
                description="Price increases automatically as tokens are purchased. No liquidity needed at launch — the curve handles it."
                active={true}
                locked={true}
              />
              <FeatureCard
                icon={Zap}
                title="Auto-LP Graduation"
                description={`When the reserve hits ${formatTokenAmount(graduationThreshold)} JASMY, the pool graduates to JasmyChain V2 automatically.`}
                active={true}
                locked={true}
              />
              <FeatureCard
                icon={Rocket}
                title="Initial Buy"
                description="Purchase tokens in the same transaction as the launch. You'll be the first buyer at the lowest bonding curve price."
                active={wantsInitialBuy}
                onClick={() => setWantsInitialBuy(!wantsInitialBuy)}
              />
            </div>

            {/* Initial buy amount — shown when toggled on */}
            {wantsInitialBuy && (
              <div className="mt-4 animate-fade-in">
                <label className="block text-sm text-text-secondary mb-1.5">
                  Buy Amount (JASMY)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={initialBuy}
                    onChange={(e) => setInitialBuy(e.target.value)}
                    placeholder="0.0"
                    className="input-field pr-16"
                    min="0"
                    step="any"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">
                    JASMY
                  </span>
                </div>
                <p className="text-[11px] text-text-tertiary mt-1">
                  You buy at the initial bonding curve price — the lowest possible price.
                </p>
              </div>
            )}
          </div>

          {/* Section 4: Cost estimate + submit */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Rocket}
              title="Cost Estimate"
              subtitle="Total JASMY required for this transaction"
            />

            <div className="bg-surface/50 border border-border rounded-card p-4 mb-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-tertiary">Launch Fee</span>
                <span className="text-text-secondary">{formatTokenAmount(launchFee)} JASMY</span>
              </div>
              {wantsInitialBuy && parsedInitialBuy > 0n && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-tertiary">Initial Buy</span>
                  <span className="text-text-secondary">{formatTokenAmount(parsedInitialBuy)} JASMY</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold border-t border-border pt-2">
                <span className="text-text-primary">Total Cost</span>
                <span className="text-primary font-display text-base">{formatTokenAmount(totalCost)} JASMY</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={!isConnected || !isValid || isPending || isConfirming}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {(isPending || isConfirming) && <Loader2 className="w-4 h-4 animate-spin" />}
              {!isConnected
                ? "Connect Wallet to Launch"
                : isPending
                ? "Awaiting Signature..."
                : isConfirming
                ? "Launching Token..."
                : "Launch Token"}
            </button>

            {txHash && !isSuccess && (
              <div className="mt-3 text-center">
                <a
                  href={explorerUrl("tx", txHash)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
                >
                  View Transaction <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {!isConnected && (
              <p className="text-center text-xs text-text-tertiary mt-3">
                Connect your wallet to launch a token on JasmyChain
              </p>
            )}
          </div>
        </form>

        {/* ── Right panel: live preview ──────────────────────────────────────── */}
        <div className="lg:col-span-2">
          <div className="sticky top-20">
            <h3 className="font-display text-sm font-semibold text-text-secondary mb-3 uppercase tracking-wider">
              Live Preview
            </h3>
            <TokenPreview
              name={name}
              symbol={symbol}
              description={description}
              imageUri={imageUri}
              buyFee={buyFeeBps}
              sellFee={sellFeeBps}
              graduationThreshold={graduationThreshold}
            />

            {/* Info panel below preview */}
            <div className="mt-4 glass-card p-4">
              <h4 className="text-xs font-semibold text-text-secondary mb-3 uppercase tracking-wider">
                How it works
              </h4>
              <div className="space-y-3 text-[11px] text-text-tertiary leading-relaxed">
                <div className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>Pay the launch fee — your token is deployed and the bonding curve initializes.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>Anyone can buy or sell on the curve. Price moves with every trade.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-4 h-4 rounded-full bg-primary/15 text-primary text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>Once the reserve hits the graduation threshold, liquidity moves to JasmyChain V2 DEX.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
